import React, { useState, useEffect } from "react";
import { Form, Select, InputNumber, Input, Typography } from "antd";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { elvis } from "../../Utils";

const { Option } = Select;
const { Title } = Typography;

const LivestockForm = props => {
  const [form] = Form.useForm();
  const [purposeTypes, setPurposeTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const { t } = useTranslation();
  const isEditMode = props.isEditMode || false;

  const formItemLayout = {
    labelCol: {
      sm: { span: 8 }
    },
    wrapperCol: {
      sm: { span: 16 }
    }
  };

  // Load types from API
  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/farm/enums/livestock-subtypes")
      .then(({ data }) => {
        setSubtypes(data);
      })
      .catch(function (error) {
        console.log(error);
      });

    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/farm/enums/livestock-purposes")
      .then(({ data }) => {
        setPurposeTypes(data);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, []);

  // Initialize form with record data
  useEffect(() => {
    if (props.record.asset) {
      form.setFieldsValue({
        age: props.record.asset.age,
        weight: props.record.asset.weight,
        yearProduction: props.record.asset.yearProduction,
        breedingCost: props.record.asset.breedingCost,
        gatePrice: props.record.asset.gatePrice,
        purpose: props.record.asset.purpose,
        livestockSubtype: props.record.asset.livestockSubtype
      });
    }
  }, [props.record.asset, form]);

  // Reset form when exiting edit mode
  useEffect(() => {
    if (!isEditMode && props.record.asset) {
      form.resetFields();
      form.setFieldsValue({
        age: props.record.asset.age,
        weight: props.record.asset.weight,
        yearProduction: props.record.asset.yearProduction,
        breedingCost: props.record.asset.breedingCost,
        gatePrice: props.record.asset.gatePrice,
        purpose: props.record.asset.purpose,
        livestockSubtype: props.record.asset.livestockSubtype
      });
    }
  }, [isEditMode, form, props.record.asset]);

  // Handle form changes and notify parent
  const handleFormChange = () => {
    if (!isEditMode) return;

    const values = form.getFieldsValue();
    const errors = form.getFieldsError();
    const isValid = errors.every(error => !error.errors.length);

    const newAsset = { ...props.record };
    newAsset.asset.age = elvis(values.age);
    newAsset.asset.weight = elvis(values.weight);
    newAsset.asset.yearProduction = elvis(values.yearProduction);
    newAsset.asset.breedingCost = elvis(values.breedingCost);
    newAsset.asset.gatePrice = elvis(values.gatePrice);
    newAsset.asset.livestockSubtype = elvis(values.livestockSubtype);
    newAsset.asset.purpose = elvis(values.purpose);
    newAsset.valid = isValid;
    props.parentCallback(newAsset);
  };

  return (
    <Form
      form={form}
      {...formItemLayout}
      className="details_form"
      onValuesChange={handleFormChange}
      onFieldsChange={handleFormChange}
    >
      <Title
        className="details_form_header"
        title="Asset details"
        subTitle="Livestock"
      />

      <Form.Item
        label="Average age"
        name="age"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <InputNumber disabled={!isEditMode} />
      </Form.Item>

      <Form.Item
        label="Average weight (kg)"
        name="weight"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <InputNumber disabled={!isEditMode} />
      </Form.Item>

      <Form.Item
        label="Year production (pz)"
        name="yearProduction"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <Input disabled={!isEditMode} />
      </Form.Item>

      <Form.Item
        label="Year breeding Cost (€)"
        name="breedingCost"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <Input disabled={!isEditMode} />
      </Form.Item>

      <Form.Item
        label="Pre-disaster value (€)"
        name="gatePrice"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <InputNumber disabled={!isEditMode} />
      </Form.Item>

      <Form.Item
        label="Type"
        name="livestockSubtype"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <Select disabled={!isEditMode}>
          {subtypes.map(name => (
            <Option key={name} value={name}>
              {t("farmEnums.livestock_subtype." + name, name)}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Breeding purpose"
        name="purpose"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <Select disabled={!isEditMode}>
          {purposeTypes.map(name => (
            <Option key={name} value={name}>
              {t("farmEnums.livestock_purpose." + name, name)}
            </Option>
          ))}
        </Select>
      </Form.Item>
    </Form>
  );
};

export default LivestockForm;